/**
 * Incidents — the hero layer.
 *
 * Shows only the trailing 24 hours. Anything older is not drawn at all: this
 * is a live board, not an archive, and a day is the window where "what is
 * happening" still means something.
 *
 * Each incident renders as a **beacon**, built from three stacked marks:
 *
 *   halo   large, very low alpha — the soft bloom that makes it glow
 *   ring   a stroked circle — the hard edge that reads as a marker
 *   core   small, bright, opaque — the thing your eye lands on
 *
 * A single flat dot disappears against a busy basemap; the halo/ring/core
 * stack stays legible over both black asphalt and a lit building face.
 */

import { ScatterplotLayer } from "@deck.gl/layers";

import { footprints } from "../map/footprints.js";
import { Layer } from "./base.js";

/**
 * Clearance above a rooftop, in metres.
 *
 * Sitting exactly on the roof puts the marker in the same plane as the surface
 * it stands on, which z-fights and tears as the camera moves. Two metres is
 * under a storey, so it still reads as belonging to that roof.
 */
const ROOF_CLEARANCE_M = 2;

/** A rooftop height turned into a position to sit at, with clearance. */
const lift = (height) => (height > 0 ? height + ROOF_CLEARANCE_M : 0);


import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/** Nothing older than this is drawn. */
export const WINDOW_MS = 24 * 3600 * 1000;

/** Freshly-arrived incidents flare for this long before settling. */
const FLARE_MS = 45 * 60 * 1000;

/**
 * Teal is the data colour: bright, cool, and nothing else on the map uses it.
 * Blue belongs to user marks and amber to the live indicator, so a glance
 * tells you which of the three you are looking at.
 */

/** Severity drives size and brightness, never hue. */
const CATEGORY = {
  violent: { scale: 1.0, level: 4, label: "Violent" },
  property: { scale: 0.78, level: 3, label: "Property" },
  disorder: { scale: 0.64, level: 2, label: "Disorder" },
  utility: { scale: 0.7, level: 2, label: "Utility" },
  traffic: { scale: 0.64, level: 1, label: "Traffic" },
  other: { scale: 0.52, level: 0, label: "Other" },
};

/**
 * The source a row came from when it does not say.
 *
 * Only live feeds tag their points. The records feed is tens of thousands of
 * rows and stamping an identical string onto every one of them would cost real
 * bytes to say what the absence already says.
 */
const RECORDS = "records";

/** How each source is named to a reader, and what its timestamp means. */
const SOURCES = {
  records: { label: "Denver Open Data", live: false },
  xcel: { label: "Xcel Energy outage map", live: true },
};

const DEFAULT = CATEGORY.other;

const BASE_RADIUS = 52;

export function humanise(value) {
  if (!value) return "Unknown";
  return String(value)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export class IncidentLayer extends Layer {
  /** Supplied by the app, which knows the building heights. */
  groundHeight = null;

  static key = "incidents";
  static label = "Incidents";
  static description = "Crime, traffic & outages, last 24 hours";

  /** The one layer that is the point of a live board. Server TTL is 15 min. */
  static refreshMs = 5 * 60 * 1000;

  /** Pull live incidents from the server. Returns {latest, serverNow}. */
  async load() {
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/incidents`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._rows = body.points ?? [];
      this.serverNow = body.server_now ?? Date.now();
      this.latest = body.latest ?? null;
      this.sources = body.sources ?? null;
      // One cursor per source. The records lag days behind and the live feeds
      // do not, so a shared cursor would have to be wrong about one of them.
      this.anchors = Object.fromEntries(
        (this.sources ?? []).filter((s) => s.latest).map((s) => [s.src, s.latest]),
      );
      this.stale = Boolean(body.stale);
      this.status = "ready";
      this.detail = `${this._rows.length} in ${14} days`;
      return { latest: this.latest, serverNow: this.serverNow };
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  rows() {
    return this._rows ?? [];
  }

  get loadedCount() {
    return this._rows?.length ?? 0;
  }

  legend() {
    // One entry, not five: every incident is the same teal and the categories
    // differ only by size, so five identical swatches would be a lie.
    return [{
      rgb: palette().incident,
      label: "Incident",
      note: "Size scales with severity; brightness fades with age. Each source "
           + "covers the 24 hours ending at its own newest record: Denver's "
           + "police records run days behind, live outages are minutes old.",
    }];
  }

  /**
   * Categories the reader has narrowed to, or null for all of them.
   *
   * Null rather than a Set holding every key: "no filter" and "every filter
   * happens to be selected" look the same on screen but are not the same
   * thing, and only the first should survive the feed changing shape.
   */
  categories = null;

  /**
   * Where a source's 24-hour window ends: that source's own newest record.
   *
   * This is the whole reason two feeds can share one layer. Denver's records
   * publish days behind, so their window has to end when their data does or
   * the map is empty. An outage is minutes old, so its window ends now. One
   * shared cursor cannot be both, and picking either would delete the other
   * from the map — anchor at wall-clock and every offence falls out of the
   * window; anchor at the records and every outage is in the future.
   */
  anchorFor(src, fallback) {
    return this.anchors?.[src ?? RECORDS] ?? fallback;
  }

  /** How old a row is against its own source's cursor, never another's. */
  ageOf(row, fallback) {
    return this.anchorFor(row.src, fallback) - row.t;
  }

  /**
   * Everything inside the 24-hour window, newest last, before any filter.
   *
   * `now` is only the fallback cursor now, for rows whose source did not
   * report a freshness of its own — including every row in a test that builds
   * the layer by hand.
   */
  inWindow(now) {
    return this.rows()
      .filter((row) => {
        const age = this.ageOf(row, now);
        return age >= 0 && age <= WINDOW_MS;
      })
      .sort((a, b) => a.t - b.t);
  }

  /**
   * What is actually on screen: the window, narrowed to the chosen categories.
   *
   * Every consumer — the beacons, the recent list, the scanner, the window
   * readout — goes through here, so the filter reaches all of them from one
   * place rather than each remembering to apply it.
   */
  recent(now) {
    const rows = this.inWindow(now);
    if (!this.categories) return rows;
    return rows.filter((row) => this.categories.has(row.c));
  }

  /**
   * Narrow to one category, or widen back out. Returns what is now in force.
   *
   * Clicking a chip from the unfiltered view *isolates* that category rather
   * than unchecking it — "traffic" should mean show me traffic, which is the
   * question someone clicking it is asking. From there, clicking another adds
   * it and clicking a lit one removes it.
   *
   * Two ways back to everything, because a filter you cannot leave is a trap:
   * clicking the last lit chip off, and lighting them all.
   */
  toggleCategory(key, available) {
    if (!this.categories) {
      this.categories = new Set([key]);
      return this.categories;
    }
    const next = new Set(this.categories);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    this.categories = (next.size === 0 || next.size >= available.length)
      ? null : next;
    return this.categories;
  }

  describe(row) {
    const style = CATEGORY[row.c] ?? DEFAULT;
    // Against real time, not the cursor. Anchoring the cursor to the newest
    // record made four-day-old incidents report as "6 h ago".
    const reference = this.serverNow ?? Date.now();
    const minutes = Math.max(0, Math.round((reference - row.t) / 60000));
    const age = minutes < 60 ? `${minutes} min ago`
      : minutes < 2880 ? `${Math.round(minutes / 60)} h ago`
      : `${Math.round(minutes / 1440)} days ago`;
    // Xcel names a city and Denver names a neighbourhood. Labelling both
    // "Neighborhood" would quietly promote "Lakewood" to one of Denver's.
    const live = (row.src ?? RECORDS) !== RECORDS;
    const where = live
      ? ["Area", humanise(row.n)]
      : ["Neighborhood", humanise(row.n)];
    return {
      title: humanise(row.k) || style.label,
      rows: [
        // Denver time, matching the readout panel. A UTC stamp here and local
        // time three inches away was two answers to one question.
        ["When", new Date(row.t).toLocaleString("en-US", {
          timeZone: "America/Denver", month: "short", day: "numeric",
          hour: "2-digit", minute: "2-digit", hour12: false,
        })],
        ["Age", age],
        ["Category", style.label],
        where,
        // Only when the source actually counts something. An offence has no
        // headcount, and a zero here would read as "nobody affected".
        ...(row.v ? [["Customers out", row.v.toLocaleString()]] : []),
        // Crashes come from the accident ledger, not the offences feed —
        // the server merges the datasets into this one stream.
        ["Source", live
          ? (SOURCES[row.src]?.label ?? row.src)
          : row.c === "traffic"
            ? "Denver Open Data · crashes"
            : "Denver Open Data · offences"],
      ],
    };
  }

  /**
   * `now` is the live cursor. Kept as a field so describe() can report an age
   * without the tooltip needing to know about the clock.
   */
  render(now) {
    this._now = now;
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }

    const data = this.recent(now);
    this.visibleCount = data.length;
    if (data.length === 0) return [];

    // Fade with age across the window so the last 24h reads as a gradient of
    // recency rather than a flat wall of dots. Age is measured against the
    // row's own source cursor: against a shared one, every outage would be
    // days "ahead" of the records and pin to full brightness forever.
    const freshness = (d) => 1 - Math.min(this.ageOf(d, now) / WINDOW_MS, 1);
    const flare = (d) => 1 + 0.9 * (1 - Math.min(this.ageOf(d, now) / FLARE_MS, 1));
    const sized = (d, mult) =>
      BASE_RADIUS * (CATEGORY[d.c] ?? DEFAULT).scale * mult * flare(d);

    const shared = {
      data,
      // Lifted onto whatever building is under it. Incidents had no lift
      // at all, so downtown they sat at street level inside the block and
      // vanished the moment a building was drawn over them.
      getPosition: (d) => [d.x, d.y, lift(this.groundHeight?.(d))],
      radiusUnits: "meters",
      // The stamp is what makes the lift survive tiles arriving. Building
      // footprints stream in after the first paint, so a height read once
      // at mount is read against an empty index — which is exactly why
      // these stayed at ground level however far you zoomed in.
      // The colours join the theme into their trigger: the accessors read
      // palette() when they run, and without the theme in here a theme switch
      // between refresh ticks left the beacons in last theme's colour for up
      // to a minute.
      updateTriggers: {
        getRadius: now,
        getFillColor: `${now}:${themeName()}`,
        getLineColor: `${now}:${themeName()}`,
        getPosition: footprints.stamp,
      },
      parameters: OVER_EVERYTHING,
    };

    return [
      // Halo — the glow. Big, faint, never picked.
      new ScatterplotLayer({
        ...shared,
        id: `${this.key}-halo`,
        getRadius: (d) => sized(d, 2.6),
        getFillColor: (d) => [...palette().incident, Math.round(26 * freshness(d)) + 4],
        radiusMinPixels: 4,
        radiusMaxPixels: 60,
        stroked: false,
        filled: true,
        pickable: false,
      }),

      // Ring — the hard edge that makes it a marker rather than a smudge.
      new ScatterplotLayer({
        ...shared,
        id: `${this.key}-ring`,
        getRadius: (d) => sized(d, 1.35),
        getFillColor: [0, 0, 0, 0],
        getLineColor: (d) => [...palette().incident, Math.round(150 * freshness(d)) + 45],
        lineWidthUnits: "pixels",
        getLineWidth: 1.4,
        radiusMinPixels: 3,
        radiusMaxPixels: 34,
        stroked: true,
        filled: false,
        pickable: true,
      }),

      // Core — bright centre. Picked too, so the easiest target is the middle.
      new ScatterplotLayer({
        ...shared,
        id: `${this.key}-core`,
        getRadius: (d) => sized(d, 0.5),
        getFillColor: (d) => {
          const f = freshness(d);
          const c = f > 0.5 ? palette().incident : palette().incidentDim;
          return [...c, Math.round(180 * f) + 70];
        },
        radiusMinPixels: 1.6,
        radiusMaxPixels: 13,
        stroked: false,
        filled: true,
        pickable: true,
      }),
    ];
  }
}
